import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';

const Admin = () => {
  const [posts, setPosts] = useState([]);
  const [view, setView] = useState({});
  const [loading, setLoading] = useState(true);
  
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  const fetchData = async (pageNumber = currentPage) => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const res = await axios.get(`/api/posts?page=${pageNumber}&pagination_items_per_page=20&order[createdAt]=DESC`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/ld+json',
        },
      });
      setPosts(res.data['hydra:member'] || []);
      setView(res.data['hydra:view'] || {});
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage]);

  const goToPage = (n) => {
    setSearchParams({ page: n });
  };
  
  const toggleReadStatus = async (post) => {
    const token = localStorage.getItem('token');
    try {
      await axios.patch(
        post['@id'],
        { isRead: !post.isRead },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/merge-patch+json',
            Accept: 'application/ld+json',
          },
        }
      );
      setPosts((prev) =>
        prev.map((p) =>
          p['@id'] === post['@id'] ? { ...p, isRead: !post.isRead } : p
        )
      );
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) return <p className="text-center mt-10 text-lg">Завантаження...</p>;

  // Обчислюємо кількість сторінок
  const lastUrl = view['hydra:last'];
  const totalPages = lastUrl
    ? new URL(lastUrl, window.location.origin).searchParams.get('page') * 1
    : currentPage;

  // Генеруємо масив номерів для довшої пагінації
  const pages = [];
  const maxButtons = 8;
  if (totalPages <= maxButtons) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1, 2);
    const start = Math.max(3, currentPage - 2);
    const end = Math.min(totalPages - 2, currentPage + 2);
    if (start > 3) pages.push('...');
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 2) pages.push('...');
    pages.push(totalPages - 1, totalPages);
  }

  return (
    <div className="w-full max-w-5xl mx-auto px-6 py-10">
      <h1 className="text-3xl font-bold mb-6 text-left">Список питань</h1>
      {posts.length === 0 ? (
        <p className="text-center text-gray-600">Немає постів.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200 shadow-md rounded">
              <thead className="bg-gray-100 text-left">
                <tr>
                  <th className="py-3 px-4 border-b">Ім’я</th>
                  <th className="py-3 px-4 border-b">Email</th>
                  <th className="py-3 px-4 border-b">Питання</th>
                  <th className="py-3 px-4 border-b">Дата</th>
                  <th className="py-3 px-4 border-b">Прочитано</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((post) => (
                  <tr
                    key={post['@id']}
                    className={`hover:bg-gray-50 ${
                      !post.isRead ? 'bg-white' : 'bg-gray-100 font-semibold'
                    }`}
                  >
                    <td className="py-3 px-4 border-b">{post.userName}</td>
                    <td className="py-3 px-4 border-b">{post.userEmail}</td>
                    <td className="py-3 px-4 border-b">{post.question}</td>
                    <td className="py-3 px-4 border-b">
                      {new Date(post.createdAt).toLocaleString()}
                    </td>
                    <td className="py-3 px-4 border-b text-center">
                      <button
                        onClick={() => toggleReadStatus(post)}
                        className={`w-6 h-6 rounded transition-colors duration-200 ${
                          post.isRead ? 'bg-green-500 text-white' : 'bg-gray-300'
                        }`}
                        title={post.isRead ? 'Позначити як непрочитане' : 'Позначити як прочитане'}
                      >
                        {post.isRead ? '✓' : ''}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-center items-center flex-wrap gap-2 mt-6">
            <button
              onClick={() => currentPage > 1 && goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
            >
              Назад
            </button>

            {pages.map((item, idx) =>
              item === '...' ? (
                <span key={idx} className="px-2">...</span>
              ) : (
                <button
                  key={idx}
                  onClick={() => goToPage(item)}
                  className={`px-3 py-1 rounded ${
                    item === currentPage
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 hover:bg-gray-300'
                  }`}
                >
                  {item}
                </button>
              )
            )}

            <button
              onClick={() => currentPage < totalPages && goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
            >
              Вперед
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Admin;
